"use client";

import { cn } from "@/lib/utils";
import { IconMenu2, IconX } from "@tabler/icons-react";
import {
  motion,
  AnimatePresence,
  useScroll,
  useMotionValueEvent,
} from "framer-motion";
import { Link } from "react-router-dom";
import Image from "@/components/ui/image";

import React, { useRef, useState } from "react";


interface NavbarProps {
  children: React.ReactNode;
  className?: string;
}

interface NavBodyProps {
  children: React.ReactNode;
  className?: string;
  visible?: boolean;
}

interface NavItemsProps {
  items: {
    name: string;
    link: string;
  }[];
  className?: string;
  onItemClick?: () => void;
  children?: React.ReactNode;
}

interface MobileNavProps {
  children: React.ReactNode;
  className?: string;
  visible?: boolean;
}

interface MobileNavHeaderProps {
  children: React.ReactNode;
  className?: string;
}

interface MobileNavMenuProps {
  children: React.ReactNode;
  className?: string;
  isOpen: boolean;
  onClose: () => void;
}

export const Navbar = ({ children, className }: NavbarProps) => {
  const { scrollY } = useScroll();
  const [visible, setVisible] = useState<boolean>(false);

  useMotionValueEvent(scrollY, "change", (latest) => {
    if (latest > 100) {
      setVisible(true);
    } else {
      setVisible(false);
    }
  });

  return (
    <motion.div
      className={cn("fixed inset-x-0 top-4 z-40 w-full px-4", className)}
    >
      {React.Children.map(children, (child) =>
        React.isValidElement(child)
          ? React.cloneElement(
              child as React.ReactElement<{ visible?: boolean }>,
              { visible },
            )
          : child,
      )}
    </motion.div>
  );
};

export const NavBody = ({ children, className, visible }: NavBodyProps) => {
  return (
    <motion.div
      animate={{
        boxShadow: visible
          ? "0 4px 24px 0 rgba(0, 0, 0, 0.1)"
          : "0 2px 12px 0 rgba(0, 0, 0, 0.05)",
        width: visible ? "40%" : "100%",
        y: visible ? 0 : 0,
      }}
      transition={{
        type: "spring",
        stiffness: 200,
        damping: 50,
      }}
      style={{
        minWidth: "860px",
      }}
      className={cn(
        "relative z-[60] mx-auto hidden w-full max-w-7xl flex-row items-center justify-between self-start rounded-full px-6 py-2 lg:flex",
        "bg-white dark:bg-black",
        "border border-gray-200 dark:border-neutral-800",
        "shadow-sm dark:shadow-[0_4px_24px_0_rgba(0,0,0,0.3)]",
        className,
      )}
    >
      {children}
    </motion.div>
  );
};

export const NavItems = ({ items, className, onItemClick, children }: NavItemsProps) => {
  const [hovered, setHovered] = useState<number | null>(null);

  return (
    <motion.div
      onMouseLeave={() => setHovered(null)}
      className={cn(
        "absolute inset-0 hidden flex-1 flex-row items-center justify-center -translate-x-8 space-x-2 text-sm font-medium text-zinc-600 dark:text-neutral-300 transition duration-200 hover:text-zinc-800 dark:hover:text-neutral-100 lg:flex lg:space-x-2",
        className,
      )}
    >
      {items.map((item, idx) => {
        const isInternalLink = item.link.startsWith("/");
        const content = (
          <>
            {hovered === idx && (
              <motion.div
                layoutId="hovered"
                className="absolute inset-0 h-full w-full rounded-full bg-gray-100 dark:bg-neutral-800/50"
              />
            )}
            <span className="relative z-20">{item.name}</span>
          </>
        );

        if (isInternalLink) {
          return (
            <Link
              onMouseEnter={() => setHovered(idx)}
              onClick={onItemClick}
              className="relative px-4 py-2 text-neutral-600 dark:text-neutral-300"
              key={`link-${idx}`}
              to={item.link}
            >
              {content}
            </Link>
          );
        }

        return (
          <a
            onMouseEnter={() => setHovered(idx)}
            onClick={onItemClick}
            className="relative px-4 py-2 text-neutral-600 dark:text-neutral-300"
            key={`link-${idx}`}
            href={item.link}
          >
            {content}
          </a>
        );
      })}
      {children}
    </motion.div>
  );
};

export const MobileNav = ({ children, className, visible }: MobileNavProps) => {
  return (
    <motion.div
      animate={{
        boxShadow: visible
          ? "0 4px 24px 0 rgba(0, 0, 0, 0.1)"
          : "0 2px 12px 0 rgba(0, 0, 0, 0.05)",
        width: visible ? "90%" : "100%",
        paddingRight: visible ? "12px" : "0px",
        paddingLeft: visible ? "12px" : "0px",
        y: visible ? 0 : 0,
      }}
      transition={{
        type: "spring",
        stiffness: 200,
        damping: 50,
      }}
      className={cn(
        "relative z-50 mx-auto flex w-full max-w-[calc(100vw-2rem)] flex-col items-center justify-between rounded-2xl px-0 py-2 lg:hidden",
        "bg-white dark:bg-black",
        "border border-gray-200 dark:border-neutral-800",
        "shadow-sm dark:shadow-[0_4px_24px_0_rgba(0,0,0,0.3)]",
        className,
      )}
    >
      {children}
    </motion.div>
  );
};

export const MobileNavHeader = ({
  children,
  className,
}: MobileNavHeaderProps) => {
  return (
    <div
      className={cn(
        "flex w-full flex-row items-center justify-between",
        className,
      )}
    >
      {children}
    </div>
  );
};

export const MobileNavMenu = ({
  children,
  className,
  isOpen,
  onClose,
}: MobileNavMenuProps) => {
  return (
    <AnimatePresence>
      {isOpen && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className={cn(
            "absolute inset-x-0 top-16 z-50 flex w-full flex-col items-start justify-start gap-4 rounded-lg bg-white dark:bg-black px-4 py-8 shadow-[0_0_24px_rgba(34,_42,_53,_0.06),_0_1px_1px_rgba(0,_0,_0,_0.05),_0_0_0_1px_rgba(34,_42,_53,_0.04),_0_0_4px_rgba(34,_42,_53,_0.08),_0_16px_68px_rgba(47,_48,_55,_0.05),_0_1px_0_rgba(255,_255,_255,_0.1)_inset] dark:shadow-[0_0_24px_rgba(0,_0,_0,_0.3),_0_1px_1px_rgba(0,_0,_0,_0.2),_0_0_0_1px_rgba(255,_255,_255,_0.05)] dark:border dark:border-neutral-800",
            className,
          )}
        >
          {children}
        </motion.div>
      )}
    </AnimatePresence>
  );
};

export const MobileNavToggle = ({
  isOpen,
  onClick,
}: {
  isOpen: boolean;
  onClick: () => void;
}) => {
  return isOpen ? (
    <IconX className="text-black dark:text-white cursor-pointer" onClick={onClick} />
  ) : (
    <IconMenu2 className="text-black dark:text-white cursor-pointer" onClick={onClick} />
  );
};

export const NavbarLogo = ({ 
  logoLight, 
  logoDark, 
  text 
}: { 
  logoLight?: string | any; 
  logoDark?: string | any; 
  text?: string;
}) => {
  // Use logoDark (ayra-logo-dark.png) as the primary logo
  // Show logoDark in light mode, logoLight in dark mode (fallback to logoDark)
  const logoToShow = logoDark || logoLight;
  const darkModeLogo = logoLight || logoDark;

  if (!logoToShow && !text) {
    return (
      <Link to="/" className="relative z-20 mr-4 flex items-center px-2 py-1 min-h-[32px]">
        <span className="font-semibold text-lg text-black dark:text-white">Ayra</span>
      </Link>
    );
  }

  return (
    <Link
      to="/"
      className="relative z-20 mr-4 flex items-center px-2 py-1 min-h-[32px]"
    >
      {logoToShow ? (
        <>
          {/* Light mode - show logoDark (ayra-logo-dark.png) */}
          <div className="block dark:hidden relative h-8 w-auto">
            <Image
              src={logoToShow}
              alt="Ayra Logo"
              width={120}
              height={32}
              className="h-full w-auto object-contain"
              priority
            />
          </div>
          {/* Dark mode - show logoLight or fallback to logoDark */}
          <div className="hidden dark:block relative h-8 w-auto">
            <Image
              src={darkModeLogo}
              alt="Ayra Logo"
              width={120}
              height={32}
              className="h-full w-auto object-contain"
              priority
            />
          </div>
        </>
      ) : (
        <span className="font-medium text-black dark:text-white">{text}</span>
      )}
    </Link>
  );
};

export const NavbarButton = ({
  href,
  as: Tag = "a",
  children,
  className,
  variant = "primary",
  ...props
}: {
  href?: string;
  as?: React.ElementType;
  children: React.ReactNode;
  className?: string;
  variant?: "primary" | "secondary" | "dark" | "green" | "gradient";
} & (
  | React.ComponentPropsWithoutRef<"a">
  | React.ComponentPropsWithoutRef<"button">
)) => {
  const baseStyles =
    "px-4 py-2 rounded-lg bg-white dark:bg-neutral-900 text-black dark:text-white text-sm font-medium relative cursor-pointer transition-all duration-200 inline-block text-center shadow-sm dark:shadow-none overflow-hidden before:absolute before:inset-0 before:pointer-events-none before:rounded-[inherit] before:shadow-[inset_0_1px_0_rgba(255,255,255,0.15)] dark:before:shadow-[inset_0_1px_0_rgba(255,255,255,0.1)]";

  const variantStyles = {
    primary:
      "bg-primary text-white dark:bg-primary dark:text-primary-foreground",
    secondary: "bg-transparent shadow-none dark:text-neutral-300",
    dark: "bg-black dark:bg-white text-white dark:text-black",
    green: "bg-[#5F8F84] hover:bg-[#4a7a6f] text-white",
    gradient:
      "bg-gradient-to-b from-blue-500 to-blue-700 text-white",
  };

  const Component = Tag as React.ComponentType<any> | keyof JSX.IntrinsicElements;
  return (
    <Component
      href={href || undefined}
      className={cn(baseStyles, variantStyles[variant], className)}
      {...(props as any)}
    >
      {children}
    </Component>
  );
};
